/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    // Hover waits for a short dwell so sweeping the pointer across the gallery doesn't
    // start a full-size download for every image it passes. Focus preloads immediately.
    this.addEventListener('pointerover', this.#handleHoverIntent, opts);
    this.addEventListener('pointerout', this.#cancelHoverIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    // The variant picker lives outside the gallery and is re-rendered on each change,
    // so listen at the document and show the chosen variant's photo.
    try {
      this.#variantMedia = JSON.parse(this.querySelector('script[data-variant-media]')?.textContent || '{}');
    } catch {
      this.#variantMedia = {};
    }
    document.addEventListener('change', this.#handleVariantChange, opts);

    // Thumbnail strip: clicking a thumbnail shows that image; swiping the main image
    // keeps the highlighted thumbnail in sync.
    this.#gallery = this.querySelector('[data-product-gallery]');
    this.#thumbs = this.querySelector('[data-gallery-thumbs]');
    this.#thumbs?.addEventListener('click', (event) => {
      const thumb = event.target instanceof Element ? event.target.closest('[data-thumb-for]') : null;
      if (thumb) this.#showMedia(thumb.dataset.thumbFor, true);
    }, opts);
    this.#gallery?.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#scrollFrame);
      this.#scrollFrame = requestAnimationFrame(() => this.#syncActiveThumb());
    }, { ...opts, passive: true });

    // Full-screen zoom: tapping anywhere (image, background or the X) closes it,
    // so buyers are never stuck, even on browsers without native command support.
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const dialog = target?.closest('dialog[id^="product-image-zoom-"]');
      if (dialog?.open) dialog.close();
    }, opts);

    const initialVariant = new URL(location.href).searchParams.get('variant');
    if (initialVariant && this.#variantMedia[initialVariant]) {
      this.#showMedia(this.#variantMedia[initialVariant], false);
    }
  }

  #variantMedia = {};
  #gallery = null;
  #thumbs = null;
  #scrollFrame = 0;

  #handleVariantChange = (event) => {
    const input = event.target;
    if (!(input instanceof Element) || !input.closest('s-variant-picker')) return;
    const variantId = input.dataset?.variantId || input.selectedOptions?.[0]?.dataset?.variantId;
    const mediaId = variantId && this.#variantMedia[variantId];
    if (mediaId) this.#showMedia(mediaId, true);
  };

  #showMedia(mediaId, smooth) {
    const gallery = this.#gallery;
    const item = gallery?.querySelector(`:scope > [data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!gallery || !item) return;
    item.querySelector('img')?.setAttribute('loading', 'eager');
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Rect difference works for both LTR and RTL scroll directions.
    const left = item.getBoundingClientRect().left - gallery.getBoundingClientRect().left + gallery.scrollLeft;
    gallery.scrollTo({ left, behavior: smooth && !reduce ? 'smooth' : 'auto' });
    this.#setActiveThumb(String(mediaId));
  }

  #syncActiveThumb() {
    const gallery = this.#gallery;
    if (!gallery || !this.#thumbs) return;
    const box = gallery.getBoundingClientRect();
    const center = box.left + box.width / 2;
    let closest = null;
    let distance = Infinity;
    for (const item of gallery.querySelectorAll(':scope > [data-media-id]')) {
      const rect = item.getBoundingClientRect();
      const d = Math.abs(rect.left + rect.width / 2 - center);
      if (d < distance) {
        distance = d;
        closest = item;
      }
    }
    if (closest) this.#setActiveThumb(closest.dataset.mediaId);
  }

  #setActiveThumb(mediaId) {
    const thumbs = this.#thumbs;
    if (!thumbs) return;
    let active = null;
    for (const thumb of thumbs.querySelectorAll('[data-thumb-for]')) {
      const isActive = thumb.dataset.thumbFor === mediaId;
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
        active = thumb;
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
    if (!active) return;
    const box = thumbs.getBoundingClientRect();
    const rect = active.getBoundingClientRect();
    if (rect.left < box.left || rect.right > box.right) {
      thumbs.scrollTo({ left: thumbs.scrollLeft + rect.left - box.left - (box.width - rect.width) / 2 });
    }
  }

  #hoverTimer = null;

  #handleHoverIntent = (event) => {
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = setTimeout(() => this.#handlePreloadIntent(event), 200);
  };

  #cancelHoverIntent = () => {
    clearTimeout(this.#hoverTimer);
  };

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#hoverTimer);

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
